import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardHeader,
    CardContent,
    Grid,
    TextField,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Typography,
    IconButton,
    Stack,
    Divider,
    Alert,
    CircularProgress,
    Autocomplete,
    Chip,
    InputAdornment
} from '@mui/material';
import { 
    Add as AddIcon, 
    Delete as DeleteIcon, 
    Save as SaveIcon,
    Search as SearchIcon,
    LocationOn as LocationIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { customerService } from '../../../services/customerService';
import { productService } from '../../../services/productService';
import { pincodeService } from '../../../services/pincodeService';

const CustomerForm = ({ customer, onSuccess, onCancel, viewMode = false }) => {
    const { handleApiCallWithLoading, showError, showInfo } = useApiHandler();
    const [loading, setLoading] = useState(false);
    const [pincodeLoading, setPincodeLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [locationInfo, setLocationInfo] = useState(null);
    const [formData, setFormData] = useState({
        cust_code: '',
        cust_name: '',
        from_name: '',
        from_company: '',
        from_email: '',
        from_address: '',
        from_city: '',
        from_state: '',
        from_mobile_no: '',
        from_pincode: '',
        bankers_details: '',
        trade_details: '',
        cust_user_name: '',
        cust_password: '',
        cust_acc_status: 'Y',
        status: '',
        cust_gst_no: '',
        credit_days: '',
        discounts: [{
            product_id: '',
            fuel_surcharge_disc: '',
            package_disc: ''
        }]
    });
    const [errors, setErrors] = useState({});
    const [validationErrors, setValidationErrors] = useState([]);

    // Generate simple sequential customer code
    const generateCustomerCode = async () => {
        try {
            const response = await customerService.getNextCustomerCode();
            if (response.success) {
                return response.data.next_code;
            }
            // Fallback if API fails
            return 'SX001';
        } catch (error) {
            console.error('Failed to get next customer code:', error);
            // Fallback if API fails
            return 'SX001';
        }
    };

    // Regenerate customer code
    const handleRegenerateCode = async () => {
        const newCode = await generateCustomerCode();
        setFormData(prev => ({ ...prev, cust_code: newCode }));
        showInfo('New customer code generated');
    };

    useEffect(() => {
        fetchProducts();
        if (customer) {
            // Extract and validate discounts
            const discounts = customer.discounts || [];
            console.log('Discounts from customer:', discounts);
            console.log('Customer data:', customer);
            
            const formattedDiscounts = discounts.length > 0 
                ? discounts.map(discount => {
                    console.log('Processing discount:', discount);
                    return {
                        product_id: discount.product?.id || discount.product_id || '',
                        fuel_surcharge_disc: discount.fuel_surcharge_disc?.toString() || '',
                        package_disc: discount.package_disc?.toString() || ''
                    };
                }).filter(discount => discount.product_id !== '') // Filter out invalid discounts
                : [{
                    product_id: '',
                    fuel_surcharge_disc: '',
                    package_disc: ''
                }];
            
            console.log('Formatted discounts:', formattedDiscounts);

            const newFormData = {
                cust_code: customer.cust_code || '',
                cust_name: customer.cust_name || '',
                from_name: customer.from_name || '',
                from_company: customer.from_company || '',
                from_email: customer.from_email || '',
                from_address: customer.from_address || '',
                from_city: customer.from_city || '',
                from_state: customer.from_state || '',
                from_mobile_no: customer.from_mobile_no || '',
                from_pincode: customer.from_pincode || '',
                bankers_details: customer.bankers_details || '',
                trade_details: customer.trade_details || '',
                cust_user_name: customer.cust_user_name || '',
                cust_password: '',
                cust_acc_status: customer.cust_acc_status || 'Y',
                status: customer.status || '',
                cust_gst_no: customer.cust_gst_no || '',
                credit_days: customer.credit_days?.toString() || '',
                discounts: formattedDiscounts
            };
            setFormData(newFormData);

            // For edit mode, populate location info from existing data only
            // Don't fetch from API to avoid overriding user's existing city/state
            if (customer.from_pincode && customer.from_city && customer.from_state) {
                setLocationInfo({
                    city: customer.from_city,
                    state: customer.from_state,
                    district: customer.from_city, // Use city as district fallback
                    region: customer.from_state, // Use state as region fallback
                    country: 'India'
                });
            }
        } else {
            // Generate customer code for new customer
            const generateCode = async () => {
                const newCode = await generateCustomerCode();
                setFormData(prev => ({ ...prev, cust_code: newCode }));
            };
            generateCode();
        }
    }, [customer]);

    const fetchProducts = async () => {
        try {
            // /products is paginated (10 by default); the package picker needs
            // every product, or anything past the first page cannot be assigned.
            const response = await productService.getAllProducts({ per_page: 100 });
            if (response.success) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch products:', error);
            showError('Failed to fetch products');
        }
    };

    const fetchLocationByPincode = async (pincode) => {
        if (!pincode || pincode.length !== 6) {
            setLocationInfo(null);
            return;
        }

        setPincodeLoading(true);
        try {
            const response = await pincodeService.getPincodeDetails(pincode);
            if (response.success) {
                setLocationInfo(response.data);
                // Auto-fill city and state
                setFormData(prev => ({
                    ...prev,
                    from_city: response.data.city,
                    from_state: response.data.state
                }));
            } else {
                setLocationInfo(null);
                showError(response.message || 'Invalid pincode');
                // Clear city and state if pincode is invalid
                setFormData(prev => ({
                    ...prev,
                    from_city: '',
                    from_state: ''
                }));
            }
        } catch (error) {
            console.error('Failed to fetch location:', error);
            setLocationInfo(null);
            showError('Failed to fetch location details');
        } finally {
            setPincodeLoading(false);
        }
    };

    const handlePincodeChange = (e) => {
        const pincode = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
        setFormData(prev => ({ ...prev, from_pincode: pincode }));
        
        // Auto-fetch location if pincode is 6 digits
        // For new customers, always fetch from API
        // For existing customers, only fetch if pincode has changed from original
        if (pincode.length === 6) {
            const shouldFetchFromAPI = !customer || (customer && pincode !== customer.from_pincode);
            
            if (shouldFetchFromAPI) {
                fetchLocationByPincode(pincode);
            } else {
                // For edit mode with unchanged pincode, restore original city/state
                setFormData(prev => ({
                    ...prev,
                    from_city: customer.from_city || '',
                    from_state: customer.from_state || ''
                }));
                setLocationInfo({
                    city: customer.from_city,
                    state: customer.from_state,
                    district: customer.from_city,
                    region: customer.from_state,
                    country: 'India'
                });
            }
        } else {
            setLocationInfo(null);
            // Clear city and state if pincode is incomplete
            setFormData(prev => ({
                ...prev,
                from_city: '',
                from_state: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setValidationErrors([]);

        try {
            if (customer) {
                const response = await customerService.updateCustomer(customer.id, formData);
                if (response.success) {
                    onSuccess();
                }
            } else {
                const response = await customerService.createCustomer(formData);
                if (response.success) {
                    onSuccess();
                }
            }
        } catch (error) {
            const message = error.response?.data?.message || `Failed to ${customer ? 'update' : 'create'} customer`;
            showError(message);
            
            // Handle validation errors
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(field => {
                    newErrors[field] = error.errors[field][0];
                });
                setErrors(newErrors);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDiscountChange = (index, field, value) => {
        // If changing product_id, validate for duplicates
        if (field === 'product_id' && value !== '') {
            const isDuplicate = formData.discounts.some((discount, i) => 
                i !== index && discount.product_id === value
            );
            
            if (isDuplicate) {
                showError('This product already has a discount configured. Please select a different product.');
                return;
            }
        }
        
        setFormData(prev => ({
            ...prev,
            discounts: prev.discounts.map((discount, i) =>
                i === index
                    ? { ...discount, [field]: value }
                    : discount
            )
        }));
    };
    
    // Get available products for a specific discount index
    const getAvailableProducts = (currentIndex) => {
        const usedProductIds = formData.discounts
            .map((discount, i) => i !== currentIndex ? discount.product_id : null)
            .filter(id => id !== null && id !== '');
        
        return products.filter(product => !usedProductIds.includes(product.id));
    };

    const addDiscount = () => {
        // Check if there are unused products available
        const usedProductIds = formData.discounts
            .map(discount => discount.product_id)
            .filter(id => id !== '');
        
        const availableProducts = products.filter(product => 
            !usedProductIds.includes(product.id)
        );
        
        if (availableProducts.length === 0) {
            showError('All products already have discounts configured. Remove an existing discount to add a new one.');
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            discounts: [
                ...prev.discounts,
                {
                    product_id: '',
                    fuel_surcharge_disc: '',
                    package_disc: ''
                }
            ]
        }));
    };

    const removeDiscount = (index) => {
        setFormData(prev => ({
            ...prev,
            discounts: prev.discounts.filter((_, i) => i !== index)
        }));
    };

    const validateForm = () => {
        const errors = {};
        const validationErrors = [];

        if (!formData.cust_code) {
            errors.cust_code = "Customer code is required";
            validationErrors.push("Customer code is required");
        }
        if (!formData.cust_name) {
            errors.cust_name = "Customer name is required";
            validationErrors.push("Customer name is required");
        }
        if (!formData.from_name) {
            errors.from_name = "From name is required";
            validationErrors.push("From name is required");
        }
        if (!formData.from_company) {
            errors.from_company = "From company is required";
            validationErrors.push("From company is required");
        }
        if (!formData.from_email) {
            errors.from_email = "From email is required";
            validationErrors.push("From email is required");
        }
        if (!formData.from_address) {
            errors.from_address = "From address is required";
            validationErrors.push("From address is required");
        }
        if (!formData.from_pincode) {
            errors.from_pincode = "Pincode is required";
            validationErrors.push("Pincode is required");
        } else if (formData.from_pincode.length !== 6) {
            errors.from_pincode = "Pincode must be 6 digits";
            validationErrors.push("Pincode must be 6 digits");
        }
        if (!formData.from_city) {
            errors.from_city = "City is required (auto-filled from pincode)";
            validationErrors.push("City is required (auto-filled from pincode)");
        }
        if (!formData.from_state) {
            errors.from_state = "State is required (auto-filled from pincode)";
            validationErrors.push("State is required (auto-filled from pincode)");
        }
        if (!formData.from_mobile_no) {
            errors.from_mobile_no = "From mobile number is required";
            validationErrors.push("From mobile number is required");
        }
        if (!formData.cust_user_name) {
            errors.cust_user_name = "Username is required";
            validationErrors.push("Username is required");
        }
        if (!customer && !formData.cust_password) {
            errors.cust_password = "Password is required";
            validationErrors.push("Password is required");
        }

        // Validate assigned packages.
        //
        // A 0% row is deliberately allowed: the row grants the customer access to
        // the package, and the discount is optional. Requiring a discount above 0
        // made "this customer may book this package at list price" impossible to
        // save, which is why no customer had any package mapped at all.
        formData.discounts.forEach((discount, index) => {
            if (discount.product_id) {
                const fuelDisc = parseFloat(discount.fuel_surcharge_disc) || 0;
                const packageDisc = parseFloat(discount.package_disc) || 0;

                if (fuelDisc < 0 || fuelDisc > 100) {
                    const errorMsg = `Discount ${index + 1}: Fuel surcharge discount must be between 0 and 100`;
                    validationErrors.push(errorMsg);
                }
                
                if (packageDisc < 0 || packageDisc > 100) {
                    const errorMsg = `Discount ${index + 1}: Package discount must be between 0 and 100`;
                    validationErrors.push(errorMsg);
                }
            }
        });

        setErrors(errors);
        setValidationErrors(validationErrors);

        return Object.keys(errors).length === 0 && validationErrors.length === 0;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Card>
                <CardHeader title="Customer Information" />
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Customer Code"
                                name="cust_code"
                                value={formData.cust_code}
                                onChange={handleChange}
                                disabled={viewMode}
                                required
                                error={!!errors.cust_code}
                                helperText={errors.cust_code || "Enter a unique code (e.g., SX001) or generate one"}
                                inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                                InputProps={{
                                    endAdornment: !viewMode && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleRegenerateCode}
                                                size="small"
                                                title="Generate new code"
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Customer Name / Company Name"
                                name="cust_name"
                                value={formData.cust_name}
                                onChange={handleChange}
                                required
                                disabled={viewMode}
                                error={!!errors.cust_name}
                                helperText={errors.cust_name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="From Name / Contact Person"
                                name="from_name"
                                value={formData.from_name}
                                onChange={handleChange}
                                required
                                disabled={viewMode}
                                error={!!errors.from_name}
                                helperText={errors.from_name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Division Name"
                                name="from_company"
                                value={formData.from_company}
                                onChange={handleChange}
                                required
                                disabled={viewMode}
                                error={!!errors.from_company}
                                helperText={errors.from_company}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="from_email"
                                type="email"
                                value={formData.from_email}
                                onChange={handleChange}
                                required
                                disabled={viewMode}
                                error={!!errors.from_email}
                                helperText={errors.from_email}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address"
                                name="from_address"
                                multiline
                                rows={3}
                                value={formData.from_address}
                                onChange={handleChange}
                                required
                                disabled={viewMode}
                                error={!!errors.from_address}
                                helperText={errors.from_address}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Pincode"
                                name="from_pincode"
                                value={formData.from_pincode}
                                onChange={handlePincodeChange}
                                required
                                disabled={viewMode}
                                error={!!errors.from_pincode}
                                helperText={errors.from_pincode || "Enter 6-digit pincode to auto-fill city & state"}
                                inputProps={{ maxLength: 6, pattern: "[0-9]*" }}
                                InputProps={{
                                    endAdornment: pincodeLoading && (
                                        <InputAdornment position="end">
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="City"
                                name="from_city"
                                value={formData.from_city}
                                onChange={handleChange}
                                disabled={viewMode}
                                error={!!errors.from_city}
                                helperText={errors.from_city || "Auto-filled from pincode (editable)"}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocationIcon color={formData.from_city ? "success" : "disabled"} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="State"
                                name="from_state"
                                value={formData.from_state}
                                onChange={handleChange}
                                disabled={viewMode}
                                error={!!errors.from_state}
                                helperText={errors.from_state || "Auto-filled from pincode (editable)"}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocationIcon color={formData.from_state ? "success" : "disabled"} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        {locationInfo && (
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2">Location Information:</Typography>
                                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip label={`District: ${locationInfo.district}`} size="small" />
                                        <Chip label={`Region: ${locationInfo.region}`} size="small" />
                                        <Chip label={`Country: ${locationInfo.country}`} size="small" />
                                    </Box>
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Mobile Number"
                                name="from_mobile_no"
                                value={formData.from_mobile_no}
                                onChange={handleChange}
                                required
                                disabled={viewMode}
                                error={!!errors.from_mobile_no}
                                helperText={errors.from_mobile_no}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="GST Number"
                                name="cust_gst_no"
                                value={formData.cust_gst_no}
                                onChange={handleChange}
                                disabled={viewMode}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Credit Days"
                                name="credit_days"
                                type="number"
                                value={formData.credit_days}
                                onChange={handleChange}
                                disabled={viewMode}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Bankers Details"
                                name="bankers_details"
                                multiline
                                rows={3}
                                value={formData.bankers_details}
                                onChange={handleChange}
                                disabled={viewMode}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Trade Details"
                                name="trade_details"
                                multiline
                                rows={3}
                                value={formData.trade_details}
                                onChange={handleChange}
                                disabled={viewMode}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ mt: 3 }}>
                <CardHeader title="Account Information" />
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Username"
                                name="cust_user_name"
                                value={formData.cust_user_name}
                                onChange={handleChange}
                                required
                                disabled={viewMode}
                                error={!!errors.cust_user_name}
                                helperText={errors.cust_user_name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Password"
                                name="cust_password"
                                type="password"
                                value={formData.cust_password}
                                onChange={handleChange}
                                required={!customer}
                                disabled={viewMode}
                                error={!!errors.cust_password}
                                helperText={customer ? 'Leave blank to keep current password' : errors.cust_password}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl>
                                <Typography variant="subtitle2" gutterBottom>
                                    Account Status
                                </Typography>
                                <RadioGroup
                                    name="cust_acc_status"
                                    value={formData.cust_acc_status}
                                    onChange={handleChange}
                                    disabled={viewMode}
                                >
                                    <FormControlLabel
                                        value="Y"
                                        control={<Radio disabled={viewMode} />}
                                        label="Active"
                                    />
                                    <FormControlLabel
                                        value="N"
                                        control={<Radio disabled={viewMode} />}
                                        label="Inactive"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ mt: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 3 }}>
                    <Box>
                        <Typography variant="h6">Packages &amp; Discounts</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {(() => {
                                const assigned = formData.discounts
                                    .map(discount => discount.product_id)
                                    .filter(id => id !== '').length;

                                // Assigning packages restricts what this customer can
                                // book, so spell out the consequence of both states.
                                return assigned === 0
                                    ? `No packages assigned. This customer can book any of the ${products.length} packages available for the route`
                                    : `${assigned} of ${products.length} packages assigned. Only these will be offered when booking for this customer`;
                            })()}
                        </Typography>
                    </Box>
                    {!viewMode && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={addDiscount}
                            disabled={(() => {
                                const usedProductIds = formData.discounts
                                    .map(discount => discount.product_id)
                                    .filter(id => id !== '');
                                return usedProductIds.length >= products.length;
                            })()}
                        >
                            Assign Package
                        </Button>
                    )}
                </Stack>

                <Divider />

                <CardContent>
                    {formData.discounts.map((discount, index) => (
                        <Box key={index} sx={{ mb: 4 }}>
                            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                <Typography variant="subtitle1">
                                    Package {index + 1}
                                    {discount.product_id && (
                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                            ({products.find(p => p.id === discount.product_id)?.name || 'Unknown Product'})
                                        </Typography>
                                    )}
                                </Typography>
                                {!viewMode && formData.discounts.length > 1 && (
                                    <IconButton
                                        color="error"
                                        onClick={() => removeDiscount(index)}
                                        title="Remove this discount"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Stack>

                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={getAvailableProducts(index)}
                                        getOptionLabel={(option) => `${option.name} (${option.code || 'N/A'})`}
                                        value={products.find(product => product.id === discount.product_id) || null}
                                        onChange={(_, newValue) => handleDiscountChange(index, 'product_id', newValue?.id || '')}
                                        disabled={viewMode}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Product/Service"
                                                required
                                                helperText={getAvailableProducts(index).length === 0 && !discount.product_id ? 
                                                    "No more products available" : 
                                                    "Packages assigned here are the only ones offered when booking for this customer"
                                                }
                                            />
                                        )}
                                        noOptionsText="No match. Products already assigned in another package row are not listed again."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {/* Spacer grid item to maintain layout */}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fuel Surcharge Discount (%)"
                                        type="number"
                                        value={discount.fuel_surcharge_disc}
                                        onChange={(e) => handleDiscountChange(index, 'fuel_surcharge_disc', e.target.value)}
                                        disabled={viewMode || !discount.product_id}
                                        InputProps={{
                                            inputProps: { min: 0, max: 100, step: 0.01 }
                                        }}
                                        helperText={!discount.product_id ? "Select a product first" : "Optional, leave blank for list price"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Package Discount (%)"
                                        type="number"
                                        value={discount.package_disc}
                                        onChange={(e) => handleDiscountChange(index, 'package_disc', e.target.value)}
                                        disabled={viewMode || !discount.product_id}
                                        InputProps={{
                                            inputProps: { min: 0, max: 100, step: 0.01 }
                                        }}
                                        helperText={!discount.product_id ? "Select a product first" : "Optional, leave blank for list price"}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            {!viewMode && (
                <Box sx={{ mt: 3 }}>
                    {validationErrors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            <Typography variant="subtitle2">Please fix the following issues:</Typography>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {validationErrors.map((error, index) => (
                                    <li key={index}>
                                        <Typography variant="body2">{error}</Typography>
                                    </li>
                                ))}
                            </ul>
                        </Alert>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {customer ? 'Update Customer' : 'Create Customer'}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default CustomerForm; 